// One CE-PE pair's valuation (backend option_valuation.py), shown the same way in the Scanner
// drawer, the trade ticket and the active-trade monitor. Display only: nothing here prices an order.
//   Strike Spread = PE strike - CE strike; Opportunity = Spread - (CE + PE); LTP is analysis,
//   the OFFER is what a trade pays. Fair value and 1-day value come from Black-Scholes (NFO, spot)
//   / Black-76 (MCX, futures) at an IV whose source is always shown; no IV -> "unavailable".
import React, { useState } from "react";
import { px } from "@/components/options/OptionsCommon";

// sign before the symbol, a true minus (−₹15,505) - the convention of every P&L figure (lib/format inrPnl)
export const rupees = (v) => (v === null || v === undefined ? "—"
  : `${Number(v) < 0 ? "−" : ""}₹${Math.abs(Number(v)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
export const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(2)}%`);
const ivPct = (v) => (v === null || v === undefined ? "—" : `${(Number(v) * 100).toFixed(2)}%`);

/** 3d 4h / 5h 12m / 14m - time to the expiry close. */
export function timeLeft(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  const s = Math.max(0, Number(seconds));
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const IV_SOURCE = {
  ATM_IMPLIED: "reference IV implied at the ATM strike", CONFIGURED: "configured reference IV",
  MARKET_IMPLIED: "implied from this leg's own price", BROKER: "broker-supplied",
};

/** Fair value: CALCULATED at an INDEPENDENT reference IV (never the leg's own IV, which would only
 *  return its own price), or n/a. Never the LTP. */
export function FairValue({ leg, testId }) {
  if (!leg || leg.fair_status === "UNAVAILABLE" || leg.fair_value === null || leg.fair_value === undefined) {
    return <span className="text-[var(--term-muted)]" title="No independent IV - fair value not computed (never replaced by the LTP or the leg's own IV)" data-testid={testId}>n/a</span>;
  }
  const own = leg.fair_reference_own_quote ? " · this IS the ATM reference strike: close to its own price by construction" : "";
  return <span title={`calculated · ${ivPct(leg.fair_iv)} (${IV_SOURCE[leg.fair_iv_source] || leg.fair_iv_source})${own}`} data-testid={testId}>
    {px(leg.fair_value)}{leg.fair_reference_own_quote ? "*" : ""}</span>;
}

/** Expiry P&L per unit at underlying S (long CE Kc + long PE Kp): max(S-Kc,0) + max(Kp-S,0) - premium. */
export function expiryAt(S, kc, kp, premium) {
  const ce = Math.max(S - kc, 0), pe = Math.max(kp - S, 0);
  return { ce, pe, value: ce + pe, pnl: premium === null || premium === undefined ? null : ce + pe - premium };
}

/** Expiry scenarios: the current underlying (default), the strikes, the midpoint and +/- one spread,
 *  plus any price the user types. Display arithmetic only - the scanner ranking is unchanged. */
export function ExpiryScenarios({ v, testPrefix }) {
  const [custom, setCustom] = useState("");
  const kc = v.ce_strike, kp = v.pe_strike, prem = v.entry_premium, mult = v.multiplier, lots = v.lots;
  if (kc === undefined || kp === undefined || kc === null || kp === null) return null;
  const spread = kp - kc;
  const base = [v.spot, kc - spread, kc, (kc + kp) / 2, kp, kp + spread].filter((x) => x !== null && x !== undefined && x > 0);
  const c = Number(custom);
  const pts = [...new Set([...(custom !== "" && c > 0 ? [c] : []), ...base].map((x) => Math.round(x * 100) / 100))];
  return (
    <div className="space-y-1" data-testid={`${testPrefix}-scenarios`}>
      <div className="flex items-center gap-2"><span className="font-semibold">Expiry P&L at underlying</span>
        <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={v.spot ? `current ${px(v.spot)}` : "price"}
          className="w-28 bg-[var(--term-input)] border border-[var(--term-border-strong)] rounded-sm px-2 py-0.5 mono text-right"
          data-testid={`${testPrefix}-scenario-input`} />
        <span className="text-[10px] text-[var(--term-muted)]">CE max(S − {kc}, 0) + PE max({kp} − S, 0) − premium {px(prem)}</span></div>
      <table className="text-[11px] mono"><thead><tr className="text-[var(--term-muted)]">
        <th className="px-1.5 text-right">S</th><th className="px-1.5 text-right">CE</th><th className="px-1.5 text-right">PE</th>
        <th className="px-1.5 text-right">Value</th><th className="px-1.5 text-right">P&L / unit</th><th className="px-1.5 text-right">/ lot</th>
        {lots ? <th className="px-1.5 text-right">× {lots} lots</th> : null}</tr></thead>
        <tbody>{pts.map((S) => {
          const e = expiryAt(S, kc, kp, prem);
          return (<tr key={S} data-testid={`${testPrefix}-scenario-${S}`} className={S === v.spot ? "font-bold" : ""}>
            <td className="px-1.5 text-right">{px(S)}{S === v.spot ? " (now)" : ""}</td><td className="px-1.5 text-right">{px(e.ce)}</td>
            <td className="px-1.5 text-right">{px(e.pe)}</td><td className="px-1.5 text-right">{px(e.value)}</td>
            <td className="px-1.5 text-right">{signed(e.pnl)}</td>
            <td className="px-1.5 text-right">{e.pnl !== null && mult ? signed(e.pnl * mult, rupees) : "—"}</td>
            {lots ? <td className="px-1.5 text-right">{e.pnl !== null && mult ? signed(e.pnl * mult * lots, rupees) : "—"}</td> : null}</tr>);
        })}</tbody></table>
    </div>
  );
}

const Line = ({ k, v, testId, strong }) => (
  <div className="flex justify-between gap-3"><span className="text-[var(--term-muted)]">{k}</span>
    <span className={`mono ${strong ? "font-bold" : ""}`} data-testid={testId}>{v}</span></div>
);

const signed = (v, f = px) => (v === null || v === undefined ? "—" : <span className={v >= 0 ? "text-[#00C805]" : "text-[#FF3B30]"}>{f(v)}</span>);

/** The full breakdown. `v`: the valuation object; `testPrefix` keeps test ids unique per place.
 *  `entryCharges`: rupees per lot of entry charges when only those are known (scanner: the broker
 *  calculator), shown as a net-after-entry-charges line; the ticket's `v.min_expiry_pnl_net_lot` is
 *  net of entry AND estimated exit charges. */
export default function PairValuation({ v, title = "Pair valuation", testPrefix = "valuation", extra = null, entryCharges = null }) {
  if (!v) return null;
  if (v.status === "UNAVAILABLE") {
    return <div className="term-panel rounded-sm p-3 text-xs text-[var(--term-muted)]" data-testid={`${testPrefix}-unavailable`}>{title}: unavailable{v.error ? ` (${v.error})` : ""}</div>;
  }
  const ce = v.ce_val || {}, pe = v.pe_val || {};
  const gross = v.min_expiry_pnl;
  return (
    <div className="term-panel rounded-sm p-3 text-xs space-y-2" data-testid={testPrefix}>
      <div className="font-bold">{title}</div>
      <div className="grid md:grid-cols-3 gap-x-6 gap-y-1">
        <div className="space-y-1">
          <Line k="Strike Spread (PE − CE)" v={px(v.strike_spread)} testId={`${testPrefix}-spread`} />
          <Line k="LTP Sum" v={px(v.ltp_sum)} />
          <Line k="Offer Sum" v={px(v.offer_sum)} />
          <Line k="Fair Value Sum" v={px(v.fair_value_sum)} />
          <Line k="LTP Opportunity / unit" v={signed(v.ltp_opportunity)} strong={v.entry_price_mode === "LTP"} testId={`${testPrefix}-ltp-opp`} />
          <Line k="Offer Opportunity / unit" v={signed(v.offer_opportunity)} testId={`${testPrefix}-offer-opp`} strong={v.entry_price_mode !== "LTP"} />
          <Line k="Fair Value Opportunity / unit" v={signed(v.fair_value_opportunity)} />
          {v.spread_to_maintain !== undefined && v.spread_to_maintain !== null &&
            <Line k={`${v.entry_price_mode === "LTP" ? "LTP" : "Offer"} Opp ≥ Spread to Maintain (${px(v.spread_to_maintain)})`}
              v={v.meets_spread_to_maintain === null || v.meets_spread_to_maintain === undefined ? "—"
                : v.meets_spread_to_maintain ? <span className="text-[#00C805] font-bold">YES</span> : <span className="text-[#FF3B30] font-bold">NO</span>}
              testId={`${testPrefix}-meets`} />}
        </div>
        <div className="space-y-1">
          <Line k="CE fair value" v={<FairValue leg={ce} testId={`${testPrefix}-ce-fv`} />} />
          <Line k="PE fair value" v={<FairValue leg={pe} testId={`${testPrefix}-pe-fv`} />} />
          <Line k="Reference IV (fair value)" v={ce.fair_iv ? `${ivPct(ce.fair_iv)} · ${IV_SOURCE[ce.fair_iv_source] || ce.fair_iv_source}` : "none - fair value n/a"} />
          <Line k="CE / PE own market IV" v={`${ivPct(ce.iv)} / ${ivPct(pe.iv)}`} testId={`${testPrefix}-own-iv`} />
          <Line k="CE / PE price − fair" v={<>{signed(ce.premium_vs_fair)} / {signed(pe.premium_vs_fair)}</>} />
          <Line k="CE / PE 1-day model value (own IV)" v={`${px(ce.one_day_value)} / ${px(pe.one_day_value)}`} />
          <Line k="CE / PE 1-day decay / unit" v={<>{signed(ce.one_day_decay)} / {signed(pe.one_day_decay)}</>} />
          <Line k="1-Day exit value at bid (pair)" v={px(v.one_day_exit_value)} />
          <Line k="Model · r" v={`${v.model === "BLACK_76" ? "Black-76 (futures)" : "Black-Scholes (spot)"} · ${pct((v.risk_free_rate || 0) * 100)}`} />
          <Line k="Time to expiry" v={timeLeft(v.seconds_to_expiry)} />
        </div>
        <div className="space-y-1">
          <Line k={`Entry premium (${v.entry_basis || "—"})`} v={px(v.entry_premium)} />
          <Line k={gross !== null && gross >= 0 ? "Minimum Gross Expiry Profit / unit" : "Minimum Gross Expiry P&L / unit"} v={signed(gross)} testId={`${testPrefix}-min-pnl`} />
          {gross !== null && v.multiplier ? <Line k="Minimum Gross Expiry P&L / lot" v={signed(gross * v.multiplier, rupees)} testId={`${testPrefix}-min-gross-lot`} /> : null}
          {v.min_expiry_pnl_net_lot !== undefined &&
            <Line k="Minimum NET Expiry P&L / lot (after entry + est. exit charges)" v={signed(v.min_expiry_pnl_net_lot, rupees)} testId={`${testPrefix}-min-net`} />}
          {v.min_expiry_pnl_net_lot === undefined && entryCharges !== null && entryCharges !== undefined && gross !== null && v.multiplier ?
            <Line k="Minimum NET P&L / lot after entry charges (exit charges not included)" v={signed(gross * v.multiplier - entryCharges, rupees)} testId={`${testPrefix}-min-net-entry`} /> : null}
          <Line k="Expiry Value (at current price)" v={px(v.expiry_value)} />
          <Line k="Expiry Profit unit / lot" v={<>{signed(v.expiry_profit_unit)} / {signed(v.expiry_profit_lot, rupees)}</>} />
          <Line k="Expiry Profit total · %" v={<>{signed(v.expiry_profit_total, rupees)} · {pct(v.expiry_profit_pct)}</>} />
          <Line k="1-Day executable unit / lot" v={<>{signed(v.one_day_profit_unit)} / {signed(v.one_day_profit_lot, rupees)}</>} />
          <Line k="1-Day executable total · %" v={<>{signed(v.one_day_profit_total, rupees)} · {pct(v.one_day_profit_pct)}</>} testId={`${testPrefix}-1d-pct`} />
          <Line k="1-Day decay unit / lot (time only)" v={<>{signed(v.one_day_decay_profit_unit)} / {signed(v.one_day_decay_profit_lot, rupees)}</>} testId={`${testPrefix}-1d-decay`} />
          <Line k="1D actual unit / lot (since prev close)" v={<>{signed(v.actual_1d_profit_unit)} / {signed(v.actual_1d_profit_lot, rupees)}</>} testId={`${testPrefix}-1d-actual`} />
        </div>
      </div>
      <ExpiryScenarios v={v} testPrefix={testPrefix} />
      {(() => {
        const net = v.min_expiry_pnl_net_lot !== undefined ? v.min_expiry_pnl_net_lot
          : (entryCharges !== null && entryCharges !== undefined && gross !== null && v.multiplier ? gross * v.multiplier - entryCharges : null);
        return gross !== null && gross >= 0 && net !== null && net < 0
          ? <div className="text-[#FF3B30] font-semibold" data-testid={`${testPrefix}-net-negative`}>The gross minimum is not negative, but after charges this pair loses money even in the best-protected case (net {rupees(net)} per lot). Slippage is not included.</div>
          : <div className="text-[10px] text-[var(--term-muted)]">Opportunity is GROSS: brokerage, taxes and slippage are not in it.</div>;
      })()}
      {extra}
      <div className="text-[10px] text-[var(--term-muted)]">
        Fair value prices each leg at an independent reference IV (a leg re-priced at its own IV only returns its own price);
        the 1-day value keeps each leg's own IV and takes one trading day off. Fair and 1-day values are model estimates, not prices you can trade; the 1-day value re-prices both legs one trading day closer to
        expiry at the same underlying price and IV. The trade is decided and sent at the live OFFER only.
      </div>
    </div>
  );
}

/** Target Profit against the allotted funds (backend option_valuation.target_plan). */
export function TargetPlan({ t, testPrefix = "target" }) {
  if (!t) return null;
  return (
    <div className="term-panel rounded-sm p-3 text-xs space-y-1" data-testid={testPrefix}>
      <div className="font-bold">Target Profit</div>
      <Line k="Target Profit" v={t.target_profit ? rupees(t.target_profit) : "not set (trade setting Profit target)"} />
      <Line k="Expected Profit per Lot (min. expiry P&L, net)" v={rupees(t.expected_profit_per_lot)} />
      <Line k="Required Lots" v={t.required_lots ?? "—"} testId={`${testPrefix}-required`} />
      <Line k="Maximum Affordable Lots" v={<>{t.max_affordable_lots ?? "—"}{t.lots_by_funds !== null && t.lots_by_funds !== undefined && t.max_lots_cap && t.lots_by_funds > t.max_lots_cap
        ? <span className="text-[var(--term-muted)]"> (funds allow {t.lots_by_funds}; capped by Max lots {t.max_lots_cap})</span> : null}</>} testId={`${testPrefix}-affordable`} />
      <Line k="Expected Total Profit" v={rupees(t.expected_total_profit)} />
      <Line k="Distance to Target Profit" v={rupees(t.distance_to_target)} />
      {t.achievable === false && <div className="text-[#FF3B30] font-semibold" data-testid={`${testPrefix}-unachievable`}>
        {t.reason === "NO_PROFIT_PER_LOT" ? "This pair has no positive net profit per lot at the current offers - the target cannot be reached."
          : t.reason === "MAX_LOTS_LIMITS_TARGET" ? "The funds would allow it, but Max lots (the account's pairs-per-trade cap) does not - the target cannot be reached."
          : "The target cannot be reached within the allotted funds."}</div>}
      {t.target_profit ? <div className="text-[10px] text-[var(--term-muted)]">
        {t.auto_exit ? "Auto-exit is ON: the trade squares off when its net unrealized P&L at the bid rises above the target (existing exit rule)."
          : "Auto-exit is OFF: the target is informational."}</div> : null}
    </div>
  );
}
